import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import useOnMount from '../../../hooks/useOnMount';
import * as ProjectsV2Store from '../../../redux/projectsV2';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import SidebarLayout from '../../layouts/SidebarLayout';
import type { RouterProps } from '../../../types';

import ContributorCountLimitWarningBanner from './ContributorCountLimitWarningBanner';
import DashboardSubnav from './DashboardSubnav';
import useFetchProjectsAction from './hooks/useFetchProjectsAction';
import useSyncStateAndURLParams from './hooks/useSyncStateAndURLParams';
import ProjectsFilters from './ProjectFilters';
import ProjectsDashboardHeader from './ProjectsDashboardHeader';
import ProjectsTable from './ProjectsTable';

import './DashboardV2.less';
import ProjectsToolbar from './ProjectsToolbar';
import ReleaseGroupLimitWarningBanner from './ReleaseGroupLimitWarningBanner';

/**
 * ProjectDashboard is a view of all the projects, along with the filters
 * to allow users to find and take action on the projects which matter to them.
 */
const ProjectDashboard: VoidFunctionComponent<RouterProps> = ({
  router,
  location,
  params,
}) => {
  const dispatch = useDispatch();
  const category = useSelector(ProjectsV2Store.selectors.getCategory);

  useOnMount(() => {
    dispatch(ProjectsV2Store.actions.clearSelection());
    dispatch(ProjectsV2Store.actions.fetchCategoriesSummary());
  });

  // Handles keeping the URL params and state in sync.
  // Note that on the initial render this will always dispatch the
  // HYDRATE_STATE action, which will kick off the initial fetch of projects.
  useSyncStateAndURLParams({ router, location, params });

  const page = useSelector(ProjectsV2Store.selectors.getPage);
  const count = useSelector(ProjectsV2Store.selectors.getCount);
  const totalItems = useSelector(ProjectsV2Store.selectors.getTotalProjects);
  const setPage = useFetchProjectsAction(ProjectsV2Store.actions.setPage);
  const setCount = useFetchProjectsAction(ProjectsV2Store.actions.setCount);

  return (
    <>
      <ProjectsDashboardHeader />
      <DashboardSubnav category={category} />
      <Helmet title="Projects" titleTemplate="%s | FOSSA" />
      <div className="projects-dashboard-v2 container" data-test-id="projectsv2-dashboard">
        <ReleaseGroupLimitWarningBanner />
        <ContributorCountLimitWarningBanner />
        <SidebarLayout>
          <SidebarLayout.Main>
            <ProjectsToolbar />
            <ProjectsTable />
            <PaginationFooter page={page} count={count} totalItems={totalItems} setPage={setPage} setCount={setCount} />
          </SidebarLayout.Main>
          <SidebarLayout.Sidebar>
            <ProjectsFilters />
          </SidebarLayout.Sidebar>
        </SidebarLayout>
      </div>
    </>
  );
};

export default ProjectDashboard;
